// ============================================
// Paper Doll — layered dressed-character preview
// Phase 3 of PAPER_DOLL.md (asset build complete 2026-06-06).
//
// Renders the equipped outfit as stacked transparent layers over a
// pose-locked body base. Assets live in assets/images/doll/ with a
// doll_ name prefix (manifest is basename-keyed; unprefixed tags would
// collide with the Items/ icons).
//
// Axes (base swap + variant suffix, mirrors appearance.js):
//   cage  -> doll_base_chastity*   (wearing_chastity)
//   hair  -> _longhair             (has_long_hair OR wearing_wig)
//   chest -> _boobs                (has_visible_breasts)
// Per-layer fallback walks most-specific -> base so a missing variant
// never blanks the garment.
// ============================================

import { events, Events } from '../engine/events.js';
import { inventoryManager } from '../systems/inventory.js';
import { variableManager } from '../engine/variables.js';
import { resolveImage, getTarStatus } from '../image-loader.js';
import { L } from '../systems/localization.js';

// z-order: mirror of the dashboard tester slot groups (D2_SLOTS).
// Lower z renders first (further back).
const Z_BY_SLOT = {
    'Legwear': 10,
    'Feet': 20,
    'Underwear': 30,
    'Lower': 40,
    'Bra': 50,
    'Piercing': 60,     // nipple/belly sit under tops; ears overridden below
    'Upper': 70,
    'Head': 140,
};
// Accessory items route by tag (Accessory slots carry mixed categories);
// also per-tag exceptions to the slot defaults.
const Z_BY_TAG = {
    // Fishnet bodysuit (neck-to-ankle sheer base layer) renders UNDER all outer
    // clothing — pants/skirt/dress/tops (owner ask 2026-06-15 "fishnet body soll
    // unter hose/kleid etc sein"). z 15 = over Legwear(10), under everything else
    // (Feet/Underwear/Lower/Bra/Upper). It's transparent, so outer garments show
    // on top while the weave shows through on exposed skin.
    fishnet_bodysuit: 15,
    // Bra-slot garments whose art extends into the HIP region (lingerie set's
    // panty half, corset's hip flare) must render UNDER bottoms or the hip
    // part paints over an equipped skirt. Mirrors the dashboard D2_SLOTS
    // order, where the whole BH group sits before Wäsche/Unten — the in-game
    // Bra(50) > Lower(40) default had drifted from it. Plain bras stay at
    // Bra(50): torso-only art, no skirt overlap. foundby:Iza (Discord,
    // 2026-07-06 "Lingerie set clips through skirt").
    lingerie_set: 25,
    corset: 25,
    // Garter belt + waist trainers (corset) render UNDER tops — owner ask
    // 2026-06-09 ("Taille/Strumpfgürtel unter dem pullover"). z 65 = over
    // Bra(50)/Piercing(60), under Upper(70).
    garter_belt: 65,
    waist_trainer_starter: 65, waist_trainer_advanced: 65, waist_trainer_expert: 65,
    // Thigh harness sits ON the thigh, UNDER pants/skirt/dress (owner ask
    // 2026-06-15 "muss unter der Hose/Kleid sein"). z 35 = over Underwear(30)
    // + Legwear(10), under Lower(40). (Body harnesses below stay OVER clothes.)
    thigh_harness: 35,
    // Fetish heels are Accessory-slot (Kitten & Chrome restock) so the Feet
    // slot default never applies — paint at the same level as Feet-slot shoes
    // (dashboard 'Schuhe' group). Without an entry they silently never render.
    heels_kitten: 20, heels_stiletto: 20, heels_platform_locking: 20,
    // Nipple clamps share the dashboard 'Brust' group with nipple_jewelry.
    nipple_clamps_light: 65, nipple_clamps_padded: 65, nipple_clamps_heavy: 65,
    // Bag hangs over the outfit (dashboard 'Hüfte': over tops, under neck gear).
    bag: 95,
    karada_harness: 90, suspension_harness: 90,
    nipple_jewelry: 65,
    choker: 100, collar: 100, masters_collar: 100, posture_collar: 100,
    ball_gag: 100, leather_collar_studded: 100,
    leash_chrome: 102,
    wrist_cuffs_leather: 110, wrist_cuffs_pink: 110, wrist_cuffs_locking: 110,
    wrist_cuffs_heavy: 110,
    ankle_cuffs_leather: 115, ankle_bracelet: 115,
    ear_studs: 120, hoop_earrings: 120,
    latex_gloves: 125,
    blindfold_satin: 135,
    hair_bow: 140, cat_ears_headband: 140,
};
// Slots that never paint a layer: Front = cage (base swap, not a layer),
// Back = plugs (front view, invisible), Item = consumables.
const SKIP_SLOTS = new Set(['Front', 'Back', 'Item']);
// Equipped intimate gear the dressed view can't show — Front (cage / belt,
// hidden behind clothing) and Back (plug / tail, behind the body in a front
// view). Surfaced as small corner chips so the player can still tell what
// they're wearing under the outfit. Owner ask 2026-06-09 ("plug etc shown in
// clothed? maybe add icons top-right"). The nude tab renders these directly
// (char_butt_plug / char_penis_chastity) so the overlay is clothed-only by
// construction (paper-doll only mounts the clothed panel + wardrobe pane).
const INDICATE_SLOTS = new Set(['Front', 'Back']);

class PaperDoll {
    constructor() {
        /** @type {Set<HTMLElement>} active mount containers */
        this._mounts = new Set();
        this._scope = events.createScope('paper-doll');
        this._renderTimer = null;
        this._bound = false;
    }

    /** Lazily bind global listeners on first mount. */
    _bind() {
        if (this._bound) return;
        this._bound = true;
        const queue = () => this._queueRender();
        this._scope.on(Events.ITEM_EQUIPPED, queue);
        this._scope.on(Events.ITEM_UNEQUIPPED, queue);
        this._scope.on(Events.BUFFS_RECALCULATED, queue);
        // axis flags: bools emit FLAG_CHANGED, numerics VARIABLE_CHANGED
        // (event-bus type asymmetry, see web-modules.md) - subscribe to both
        const onVar = (name) => {
            if (name === 'wearing_chastity' || name === 'has_long_hair'
                || name === 'has_visible_breasts' || name === 'wearing_wig') queue();
        };
        this._scope.on(Events.VARIABLE_CHANGED, onVar);
        this._scope.on(Events.FLAG_CHANGED, onVar);
        // Story-driven axis writes (salon `has_long_hair`, Spa/overnight
        // `has_visible_breasts`, scene `wearing_chastity`) never reach the
        // listeners above: the inkjs observer is suppressed for the whole
        // _processStoryContent window (blockObservers) and syncFromInk
        // repairs the mirror silently. Re-check the axes at the end of every
        // refreshView instead — same hook the character panel uses for the
        // identical class (main.js STORY_CHOICES_READY, foundby:Anne-Sophie).
        // Render only when the axis tuple actually changed.
        this._scope.on(Events.STORY_CHOICES_READY, () => {
            if (!this._mounts.size) return;
            const a = this._axes();
            const key = `${a.cage}|${a.longhair}|${a.boobs}`;
            if (key !== this._lastAxisKey) {
                this._lastAxisKey = key;
                queue();
            }
        });
    }

    /**
     * Mount the doll into a container (idempotent). Multiple mounts are
     * supported (character panel + wardrobe preview share one instance).
     */
    mount(container) {
        if (!container || this._mounts.has(container)) return;
        container.classList.add('paper-doll');
        this._mounts.add(container);
        this._bind();
        this._renderInto(container);
    }

    unmount(container) {
        if (!container) return;
        this._mounts.delete(container);
        container.innerHTML = '';
    }

    destroy() {
        this._scope.destroy();
        this._mounts.clear();
        this._bound = false;
        clearTimeout(this._renderTimer);
        clearTimeout(this._retryTimer);
    }

    /** Debounced re-render of every active mount (equip bursts, presets). */
    _queueRender() {
        if (!this._mounts.size) return;
        clearTimeout(this._renderTimer);
        this._renderTimer = setTimeout(() => {
            for (const c of this._mounts) {
                if (!c.isConnected) { this._mounts.delete(c); continue; }
                this._renderInto(c);
            }
        }, 80);
    }

    /** Current axis state. Ink-only reads use the §7 panel-read pattern. */
    _axes() {
        const vs = window._sm?.currentStory?.variablesState;
        const flag = (n) => {
            try { return !!(variableManager.getFlag(n) || (vs && vs[n])); }
            catch (e) { return false; }
        };
        return {
            cage: flag('wearing_chastity'),
            longhair: flag('has_long_hair') || flag('wearing_wig'),
            boobs: flag('has_visible_breasts'),
        };
    }

    _suffix(axes) {
        return (axes.longhair ? '_longhair' : '') + (axes.boobs ? '_boobs' : '');
    }

    /**
     * Resolve a doll asset name against the manifest with axis fallback.
     * Returns the manifest-known name (without doll_ prefix dropped) or null.
     */
    _resolveLayer(tag, axes) {
        const m = window._imageManifest;
        const candidates = [];
        const sfx = this._suffix(axes);
        candidates.push(tag + sfx);
        if (axes.longhair && axes.boobs) {
            candidates.push(tag + '_longhair', tag + '_boobs');
        }
        if (sfx) candidates.push(tag);
        for (const c of candidates) {
            const full = 'doll_' + c;
            if (!m || m[full] !== undefined) return full;
        }
        return null;
    }

    /** Build the layer list: base + one layer per equipped, z-sorted. */
    _layers() {
        const axes = this._axes();
        const sfx = this._suffix(axes);
        const list = [];
        // base (cage variant falls back to nude base if missing)
        const baseName = 'doll_base' + (axes.cage ? '_chastity' : '') + sfx;
        const m = window._imageManifest;
        const baseOk = !m || m[baseName] !== undefined;
        list.push({ name: baseOk ? baseName : ('doll_base' + sfx), z: 0 });
        let equipped = {};
        try { equipped = inventoryManager.getEquippedItems() || {}; } catch (e) {}
        for (const [slot, item] of Object.entries(equipped)) {
            if (!item || !item.tag) continue;
            const baseSlot = slot.replace(/\d+$/, '');   // Accessory3 -> Accessory
            if (SKIP_SLOTS.has(baseSlot)) continue;
            const layer = this._resolveLayer(item.tag, axes);
            if (!layer) continue;                        // no doll art: silent skip
            const z = Z_BY_TAG[item.tag] ?? Z_BY_SLOT[baseSlot];
            if (z === undefined) continue;               // unmapped category
            list.push({ name: layer, z });
        }
        list.sort((a, b) => a.z - b.z);
        return list;
    }

    /**
     * Equipped Front/Back items the doll can't paint, as corner-chip data.
     * At most one Front + one Back (single slots), so 0–2 chips.
     */
    _wornIndicators() {
        let equipped = {};
        try { equipped = inventoryManager.getEquippedItems() || {}; } catch (e) {}
        const out = [];
        for (const [slot, item] of Object.entries(equipped)) {
            if (!item || !item.tag) continue;
            const baseSlot = slot.replace(/\d+$/, '');
            if (!INDICATE_SLOTS.has(baseSlot)) continue;
            let name = item.displayName || item.name || item.tag;
            try { name = L.getItem(item.tag, 'name', name); } catch (e) {}
            out.push({ tag: item.icon || item.tag, name });
        }
        return out;
    }

    /** Append the worn-gear chip overlay (top-right) into a rendered doll. */
    _renderIndicators(container, indicators) {
        if (!indicators.length) return;
        const wrap = document.createElement('div');
        wrap.className = 'doll-worn-indicators';
        for (const ind of indicators) {
            const chip = document.createElement('div');
            chip.className = 'doll-worn-chip';
            chip.title = ind.name;
            const url = resolveImage('Items', ind.tag);
            if (url) {
                const img = document.createElement('img');
                img.alt = ind.name;
                img.decoding = 'async';
                img.src = url;
                // A missing icon just drops the glyph — never poisons the
                // layer-completeness retry below (separate render path).
                img.onerror = () => { img.remove(); };
                chip.appendChild(img);
            }
            wrap.appendChild(chip);
        }
        container.appendChild(wrap);
    }

    _renderInto(container) {
        const layers = this._layers();
        const indicators = this._wornIndicators();
        // Skip when the layer list AND the worn-gear set are both unchanged.
        // Equipping a plug/cage changes no layer (Front/Back are skipped) but
        // must still toggle its chip, so the signature folds both in.
        const want = layers.map(l => l.name).join('|')
            + '#worn:' + indicators.map(i => i.tag).join(',');
        if (container.dataset.dollState === want) return;
        // Tar-race self-heal (2026-06-06): the boot-time mount renders
        // BEFORE images.tar is loaded - resolveImage() then returns null
        // (tar known but not ready) or a loose path that 404s in tar-mode
        // builds. Without a retry the failed render poisons dollState and
        // every later same-outfit render short-circuits: panel stays
        // blank forever. Bounded retry until the tar delivers (~10s cap).
        const retry = () => {
            if ((this._retries = (this._retries || 0) + 1) > 20) return;
            delete container.dataset.dollState;
            clearTimeout(this._retryTimer);
            this._retryTimer = setTimeout(() => this._queueRender(), 500);
        };
        // Doll layers live ONLY in the tar (doll/ ships no loose copies, unlike
        // UI/). While the core tar streams (~10s on slow mobile), resolveImage
        // hands back a loose doll/*.png that 404s. Wait for the tar instead of
        // appending a 404-ing <img> — those boot-time 404s polluted the
        // image-loader bug reports (split-tar 0.2.8.1.2). Separate, generous
        // bound from the failure-retry: this is a pending resource, not an error.
        const ts = getTarStatus();
        if (ts.mode === 'tar' && !ts.loaded) {
            if ((this._tarWait = (this._tarWait || 0) + 1) <= 60) {
                clearTimeout(this._retryTimer);
                this._retryTimer = setTimeout(() => this._queueRender(), 500);
            }
            return;
        }
        this._tarWait = 0;
        container.innerHTML = '';
        let complete = true;
        for (const l of layers) {
            const url = resolveImage('doll', l.name);
            if (!url) { complete = false; continue; }
            const img = document.createElement('img');
            img.className = 'paper-doll-layer';
            img.alt = '';
            img.decoding = 'async';
            img.src = url;
            img.onload = () => { this._retries = 0; };
            img.onerror = () => { img.remove(); retry(); };
            container.appendChild(img);
        }
        this._renderIndicators(container, indicators);
        if (complete) container.dataset.dollState = want;
        else retry();
    }
}

export const paperDoll = new PaperDoll();
